import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';
import { environment } from '../../environments/environment';

/**
 * Backend PDF Extraction Response Interface
 */
export interface BackendPdfExtractionResponse {
  success: boolean;
  data?: {
    [key: string]: any;
  };
  error?: string;
  message?: string;
}

/**
 * PDF Extraction API Service
 * 
 * Communicates with backend endpoint: POST /pdf/extract-policy
 * Handles file upload and extraction of policy data from PDFs
 */
@Injectable({
  providedIn: 'root'
})
export class PdfExtractionApiService {
  
  // Backend API endpoint
  private readonly apiEndpoint = `${this.getApiBaseUrl()}/pdf/extract-policy`;
  
  // Request timeout (30 seconds)
  private readonly requestTimeout = 30000;

  constructor(private http: HttpClient) { }

  /**
   * Get API base URL from environment
   */
  private getApiBaseUrl(): string {
    // Check if environment has apiUrl configured
    if ((environment as any).apiUrl) {
      return (environment as any).apiUrl;
    }
    // Fallback to current location
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    const port = window.location.port ? `:${window.location.port}` : '';
    return `${protocol}//${hostname}${port}`;
  }

  /**
   * Extract data from PDF file using backend service
   * 
   * @param file PDF file to extract data from
   * @returns Observable with extraction response
   */
  extractDataFromPdf(file: File): Observable<BackendPdfExtractionResponse> {
    // Create FormData for multipart/form-data request
    const formData = new FormData();
    formData.append('pdf', file);

    console.log(`[PDF Extraction API] Uploading file: ${file.name} (${(file.size / (1024 * 1024)).toFixed(2)} MB)`);
    console.log(`[PDF Extraction API] Endpoint: ${this.apiEndpoint}`);

    return this.http.post<BackendPdfExtractionResponse>(
      this.apiEndpoint,
      formData,
      {
        // Report progress events
        reportProgress: true,
        responseType: 'json' as any // Ensure JSON response
      }
    ).pipe(
      timeout(this.requestTimeout),
      catchError(error => this.handleError(error))
    );
  }

  /**
   * Handle HTTP errors
   * 
   * @param error HTTP error response
   * @returns Error message observable
   */
  private handleError(error: HttpErrorResponse | any): Observable<never> {
    let errorMessage = 'An error occurred during PDF extraction';

    if (error instanceof HttpErrorResponse) {
      // Server-side error
      if (error.error instanceof ErrorEvent) {
        // Client-side error
        errorMessage = `Error: ${error.error.message}`;
      } else {
        // Server returned an error response
        errorMessage = error.error?.error || error.error?.message || 
                      `Server Error: ${error.status} - ${error.statusText}`;
        
        // Special handling for specific status codes
        switch (error.status) {
          case 400:
            errorMessage = 'Bad Request: Invalid file or missing required data';
            break;
          case 413:
            errorMessage = 'File is too large (max 50MB)';
            break;
          case 415:
            errorMessage = 'Unsupported file type. Only PDF files are allowed';
            break;
          case 500:
            errorMessage = 'Server error: Failed to process PDF. The file may be corrupted or in an unsupported format';
            break;
        }
      }
    } else if (error.name === 'TimeoutError') {
      errorMessage = 'Request timeout: The extraction process took too long. Try with a smaller PDF';
    } else if (error.status === 0) {
      errorMessage = 'Network error: Unable to reach the server. Ensure the backend is running';
    }

    console.error('[PDF Extraction API] Error:', errorMessage, error);
    return throwError(() => new Error(errorMessage));
  }

  /**
   * Check if backend API is available
   * 
   * @returns Observable with availability status
   */
  checkApiHealth(): Observable<{ available: boolean }> {
    return this.http.get<{ available: boolean }>(
      `${this.getApiBaseUrl()}/health`,
      { responseType: 'json' as any }
    ).pipe(
      timeout(5000),
      catchError(() => throwError(() => new Error('API is not available')))
    );
  }
}
